// Comma-separated allowlist of emails (dev/test only) that skip MSAL auth even
// when global bypass is off, so a single account can be tested without opening
// bypass to everyone.
const bypassEmails = (import.meta.env.VITE_AUTH_BYPASS_EMAILS ?? '')
  .split(',')
  .map((value: string) => value.trim().toLowerCase())
  .filter((value: string) => value.length > 0);

/** Whether authentication is globally bypassed (dev/test environments only). */
export const isAuthBypassEnabled = import.meta.env.VITE_BYPASS_AUTH === 'true';

/**
 * Determines whether a given account should bypass authentication.
 *
 * Returns `true` immediately if bypass is globally enabled. Otherwise checks
 * whether the account's username or ID token claims match an email in the
 * configured bypass allowlist.
 *
 * @param account - The MSAL account to check, or `undefined` if no account is signed in.
 * @returns `true` if authentication should be bypassed for this account.
 */
export function isUserBypassEnabled(account?: { username?: string; idTokenClaims?: unknown }): boolean {
  if (isAuthBypassEnabled) {
    return true;
  }

  if (bypassEmails.length === 0 || !account) {
    return false;
  }

  const claims = (account.idTokenClaims ?? {}) as {
    preferred_username?: string;
    email?: string;
    upn?: string;
  };

  const candidates = [
    account.username,
    claims.preferred_username,
    claims.email,
    claims.upn,
  ]
    .map((value) => value?.trim().toLowerCase())
    .filter((value): value is string => Boolean(value));

  return candidates.some((value) => bypassEmails.includes(value));
}
